import {useRef} from "react";
import {motion} from "framer-motion";
import {B,PILLARS,industryCallouts} from "../data/pillars";
import {getRiskLevel,getRecs} from "../data/recommendations";
import Gauge from "../components/Gauge";
import PillarBar from "../components/PillarBar";
import MaturityRoadmap from "../components/MaturityRoadmap";
import SectionHeader from "../components/SectionHeader";
import ServiceCard from "../components/ServiceCard";
import RecCard from "../components/RecCard";
import ROICalculator from "../components/ROICalculator";
import UrgencyMatrix from "../components/UrgencyMatrix";
import BenchmarkBanner from "../components/BenchmarkBanner";
import RiskExposurePanel from "../components/RiskExposurePanel";
import EdgeCheckCTA from "../components/EdgeCheckCTA";

export default function ReportPage({intake,answers,confidenceMap,pillarScores,onEdit}) {
  const reportRef=useRef(null);
  const avgScore=Object.values(pillarScores).reduce((a,b)=> a + b,0) / PILLARS.length;
  const risk=getRiskLevel(avgScore);
  const recs=getRecs(pillarScores,intake.vertical);
  const targetScore=Math.min(avgScore + 2.5,4.5);
  const months=avgScore < 2 ? 18 : avgScore < 3 ? 12 : 9;
  const unsureCount=Object.values(confidenceMap).filter(v=> v===false).length;

  return (
    <div ref={reportRef} style={{minHeight: "100vh",background: B.navy,fontFamily: "'Segoe UI',system-ui,sans-serif",color: B.white}}>
      {/* NAV */}
      <div style={{display: "flex",justifyContent: "space-between",alignItems: "center",padding: "14px 24px",borderBottom: "1px solid #1E3A5F",position: "sticky",top: 0,background: B.navy,zIndex: 10,flexWrap: "wrap",gap: 10}}>
        <button onClick={onEdit} style={{background: "none",border: "none",color: B.subText,cursor: "pointer",fontSize: 13,flexShrink: 0}}>← Edit</button>
        <div style={{color: B.subText,fontSize: 11,fontWeight: 700,letterSpacing: 2,textAlign: "center"}}>OML MATURITY REPORT · {new Date().toLocaleDateString()}</div>
        <button onClick={()=> window.print()} style={{background: `linear-gradient(135deg,${B.blue},${B.darkBlue})`,border: "none",borderRadius: 8,padding: "8px 18px",color: B.white,fontWeight: 700,fontSize: 13,cursor: "pointer",flexShrink: 0}}>
          ↓ Export PDF
        </button>
      </div>

      <div style={{maxWidth: 900,margin: "0 auto",padding: "32px 24px 80px"}}>
        {/* COVER */}
        <motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} style={{background: `linear-gradient(135deg,#1A2E4A,#0D1B2E)`,borderRadius: 16,border: "1px solid #1E3A5F",padding: "clamp(24px,5vw,40px)",marginBottom: 24,boxSizing: "border-box",overflow: "hidden"}}>
          <div style={{display: "flex",alignItems: "center",gap: 10,marginBottom: 20,flexWrap: "wrap"}}>
            <div style={{background: B.blue,color: B.white,fontWeight: 800,fontSize: 11,padding: "5px 12px",borderRadius: 5,letterSpacing: 1}}>VISUAL EDGE IT</div>
            <div style={{color: B.subText,fontSize: 11,letterSpacing: 1}}>TECHNOLOGY MATURITY ASSESSMENT</div>
            {unsureCount > 0 && (
              <div style={{background: B.yellow + "20",border: `1px solid ${B.yellow}40`,color: B.yellow,fontSize: 11,fontWeight: 700,padding: "4px 10px",borderRadius: 6}}>
                ⚠ {unsureCount} item{unsureCount > 1 ? "s" : ""} need validation
              </div>
            )}
          </div>
          <h1 style={{fontSize: "clamp(24px,5vw,40px)",fontWeight: 900,margin: "0 0 6px",lineHeight: 1.1,wordBreak: "break-word"}}>
            {intake.company} Technology
          </h1>
          <h1 style={{fontSize: "clamp(24px,5vw,40px)",fontWeight: 900,margin: "0 0 32px",color: B.accent,lineHeight: 1.1}}>
            Maturity Roadmap
          </h1>
          <div style={{display: "flex",flexWrap: "wrap",gap: 20}}>
            {[
              {label: "Account",val: intake.company},
              {label: "Date",val: new Date().toLocaleDateString()},
              {label: "Vertical",val: intake.vertical},
              {label: "Employees",val: intake.employees},
              {label: "OML Score",val: avgScore.toFixed(1),color: risk.color},
              {label: "Risk Level",val: risk.label,color: risk.color},
              ...(intake.rep ? [{label: "VEIT Rep",val: intake.rep}] : []),
            ].map(f=> (
              <div key={f.label} style={{flex: "1 1 120px",minWidth: 110}}>
                <div style={{fontSize: 10,color: B.subText,fontWeight: 700,letterSpacing: 1.5,textTransform: "uppercase",marginBottom: 4}}>{f.label}</div>
                <div style={{fontSize: 15,fontWeight: 700,color: f.color || B.white,wordBreak: "break-word"}}>{f.val}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* BENCHMARK BANNER */}
        <BenchmarkBanner vertical={intake.vertical} avgScore={avgScore} />

        {/* SECTION 01 */}
        <div style={{background: "#1A2E4A",borderRadius: 16,border: "1px solid #1E3A5F",padding: "clamp(20px,4vw,32px)",marginBottom: 24,boxSizing: "border-box"}}>
          <SectionHeader num={1} title="Executive Summary" sub="High-Level Maturity Profile" />
          <div style={{display: "flex",flexWrap: "wrap",gap: 28,alignItems: "flex-start",marginBottom: 24}}>
            <div style={{textAlign: "center",flexShrink: 0}}>
              <Gauge score={avgScore} max={5} size={150} color={risk.color} />
              <div style={{background: risk.color,color: B.white,fontSize: 11,fontWeight: 800,padding: "4px 14px",borderRadius: 4,marginTop: 8,display: "inline-block"}}>{risk.label}</div>
            </div>
            <div style={{flex: "1 1 300px"}}>
              <p style={{color: B.lightGray,fontSize: 15,lineHeight: 1.7,margin: "0 0 16px"}}>
                <strong style={{color: B.white}}>{intake.company}</strong> is currently operating at OML {avgScore.toFixed(1)} —{" "}
                <strong style={{color: risk.color}}>{risk.label.toLowerCase()}</strong>.
                {avgScore < 2 ? " Critical gaps across security,operations,and infrastructure expose the business to immediate risk of breach,downtime,or compliance failure." :
                  avgScore < 3 ? " Technology gaps in key areas create meaningful operational and security risk that requires structured remediation." :
                  " Core infrastructure is in place,but optimization opportunities exist to reduce cost,improve security posture,and enable strategic growth."}
              </p>
              <div style={{background: "#122240",border: `1px solid ${B.blue}40`,borderRadius: 10,padding: "14px 18px"}}>
                <div style={{fontSize: 11,color: B.accent,fontWeight: 700,letterSpacing: 2,marginBottom: 8}}>STRATEGIC GOAL</div>
                <p style={{color: B.white,fontSize: 14,lineHeight: 1.6,margin: 0}}>
                  Move {intake.company} from OML {avgScore.toFixed(1)} to OML {targetScore.toFixed(1)} within {months} months — standardizing the security stack,modernizing infrastructure,and building an AI-ready managed technology foundation.
                </p>
              </div>
            </div>
          </div>
          <RiskExposurePanel avgScore={avgScore} pillarScores={pillarScores} employees={intake.employees} />
          <MaturityRoadmap currentScore={avgScore} targetScore={targetScore} />
          <div style={{marginBottom: 8}}>
            <div style={{fontSize: 11,color: B.subText,fontWeight: 700,letterSpacing: 2,textTransform: "uppercase",marginBottom: 16}}>Pillar Score Breakdown</div>
            {PILLARS.map(p=> <PillarBar key={p.id} label={`${p.icon} ${p.label} (${p.sub})`} score={pillarScores[p.id]} color={p.color} />)}
          </div>
          {intake.vertical && industryCallouts[intake.vertical] && (
            <div style={{background: B.blue + "15",border: `1px solid ${B.blue}40`,borderRadius: 10,padding: "14px 18px",marginTop: 20}}>
              <div style={{fontSize: 11,color: B.accent,fontWeight: 700,letterSpacing: 2,marginBottom: 6}}>⚖️ {intake.vertical.toUpperCase()} COMPLIANCE CONTEXT</div>
              <p style={{color: B.lightGray,fontSize: 13,lineHeight: 1.6,margin: 0}}>{industryCallouts[intake.vertical]}</p>
            </div>
          )}
        </div>

        {/* SECTION 02 */}
        <div style={{background: "#1A2E4A",borderRadius: 16,border: "1px solid #1E3A5F",padding: "clamp(20px,4vw,32px)",marginBottom: 24,boxSizing: "border-box"}}>
          <SectionHeader num={2} title="The VEIT Advantage" sub="Integrated Solutions to Bridge Your Maturity Gaps" />
          <p style={{color: B.lightGray,fontSize: 14,lineHeight: 1.7,marginBottom: 24}}>Visual Edge IT provides an integrated ecosystem of services designed to move clients up the OML framework. Based on your profile,we recommend the following service bundles:</p>
          <div style={{display: "grid",gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",gap: 16}}>
            <ServiceCard icon="⚙️" title="Managed IT Services" sub="Proactive NOC & Help Desk" subColor={B.green}
              desc="We replace reactive maintenance with 24/7/365 RMM. Our US-based Help Desk provides immediate relief while our NOC ensures patches and updates are applied during off-hours."
              tags={["24/7/365 Monitoring","Unlimited Help Desk","Automated Patch Mgmt","Asset Lifecycle"]} />
            <ServiceCard icon="🔒" title="Cybersecurity & Compliance" sub="Defense-in-Depth Architecture" subColor={B.red}
              desc="We move you from basic antivirus to an enterprise-grade security stack. MDR/XDR with human analyst oversight for endpoint threat hunting, MFA for identity protection, and continuous vulnerability scanning."
              tags={["MDR/XDR + Human SOC","Multi-Factor Auth","Phishing Simulation","SOC-as-a-Service"]} />
            <ServiceCard icon="🖨️" title="Managed Print & Document" sub="Fleet Optimization & Digital Workflow" subColor={B.orange}
              desc="We eliminate hidden print costs. Our MPS solution automates supply fulfillment while our DMS platform digitizes paper workflows to increase velocity and auditability."
              tags={["Zero-Touch Supply","Secure Badge-Release Print","Digital Doc Archiving","AP/AR Automation"]} />
            <ServiceCard icon="🤖" title="AI & Process Automation" sub="Intelligence-First Operations" subColor="#9B59B6"
              desc="We prepare your organization for the AI-first economy. Our AI Launchpad deploys M365 Copilot,builds Power Automate workflows,and establishes data governance."
              tags={["M365 Copilot Deploy","Power Automate Builds","AI Governance Policy","Copilot Readiness"]} />
            <ServiceCard icon="☁️" title="Cloud Modernization" sub="Cloud-First Infrastructure" subColor={B.blue}
              desc="We migrate your infrastructure to cloud-first architecture. From M365 optimization to Azure deployment,we eliminate on-prem technical debt."
              tags={["M365 Migration","Azure Infrastructure","SharePoint Build-Out","Cloud Security Baseline"]} />
            <ServiceCard icon="📊" title="vCIO & Strategic Advisory" sub="Fractional Executive Leadership" subColor={B.teal}
              desc="We act as your strategic partner,not just a vendor. Our vCIOs conduct Quarterly Business Reviews to align IT spend with your revenue goals."
              tags={["Quarterly Business Reviews","IT Budget Optimization","Compliance Roadmapping","Tech Lifecycle Planning"]} />
          </div>
        </div>

        {/* SECTION 03 */}
        <UrgencyMatrix answers={answers} pillarScores={pillarScores} confidenceMap={confidenceMap} />

        {/* SECTION 04 */}
        <div style={{background: "#1A2E4A",borderRadius: 16,border: "1px solid #1E3A5F",padding: "clamp(20px,4vw,32px)",marginBottom: 24,boxSizing: "border-box"}}>
          <SectionHeader num={4} title="Strategic Recommendations" sub="90-Day Remediation Plan" />
          {recs.length===0
            ? <div style={{color: B.lightGray,fontSize: 14,padding: 20,textAlign: "center"}}>Strong posture — continue with vCIO advisory for optimization planning.</div>
            : recs.map((r,i)=> <RecCard key={i} num={i + 1} rec={r} pillarScore={pillarScores[r.pillar]} />)}
        </div>

        {/* SECTION 05 */}
        <div style={{background: "#1A2E4A",borderRadius: 16,border: "1px solid #9B59B640",padding: "clamp(20px,4vw,32px)",marginBottom: 24,boxSizing: "border-box"}}>
          <SectionHeader num={5} title="AI & Automation Maturity" sub="Intelligence Readiness Assessment" />
          <div style={{display: "flex",flexWrap: "wrap",gap: 24,alignItems: "flex-start"}}>
            <Gauge score={pillarScores.ai} max={5} size={120} color="#9B59B6" />
            <div style={{flex: "1 1 280px"}}>
              <p style={{color: B.lightGray,fontSize: 14,lineHeight: 1.7,margin: "0 0 16px"}}>
                {pillarScores.ai < 2 ? `${intake.company} has no AI tools deployed and processes are largely undocumented.` :
                  pillarScores.ai < 3 ? `${intake.company} is exploring AI concepts but has not yet standardized tooling or governance.` :
                  `${intake.company} has piloted AI tools and is building toward an intelligence-first operation.`}
              </p>
              <div style={{background: "#122240",borderRadius: 10,padding: "14px 18px"}}>
                <div style={{fontSize: 11,color: "#9B59B6",fontWeight: 700,letterSpacing: 2,marginBottom: 8}}>AI READINESS GAPS</div>
                {PILLARS.find(p=> p.id==="ai").questions.map(q=> {
                  const score=(answers[q.id] || 0) + 1;
                  const isUnsure=confidenceMap[q.id]===false;
                  const c=score < 2 ? B.red : score < 3 ? B.orange : score < 4 ? B.yellow : B.green;
                  return (
                    <div key={q.id} style={{display: "flex",justifyContent: "space-between",alignItems: "center",padding: "6px 0",borderBottom: "1px solid #1E3A5F",gap: 8}}>
                      <span style={{color: B.lightGray,fontSize: 13}}>{q.label}</span>
                      <div style={{display: "flex",gap: 6,alignItems: "center"}}>
                        {isUnsure && <span style={{color: B.yellow,fontSize: 10,fontWeight: 700}}>⚠ VALIDATE</span>}
                        <span style={{color: c,fontSize: 13,fontWeight: 700}}>OML {score}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 06 */}
        <ROICalculator employees={intake.employees} avgScore={avgScore} />

        {/* EDGECHECK CTA */}
        <EdgeCheckCTA intake={intake} avgScore={avgScore} />
      </div>
    </div>
  );
}